import { useEffect, useRef } from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';
import Select from '~/components/Select';
import CheckboxSelector from '~/components/CheckboxSelector';
import ErrorMessage from '~/components/ErrorMessage';

export const operators = {
    '=': {
        name: 'is',
    },
    '!=': {
        name: 'is not',
    },
    '>': {
        name: '>',
    },
    '>=': {
        name: '>=',
    },
    '<': {
        name: '<',
    },
    '<=': {
        name: '<=',
    },
    contains: {
        name: 'contains',
    },
    empty: {
        name: 'is empty',
        // Only the left side matters, there is nothing to compare against
        noComparison: true,
    },
    'not empty': {
        name: 'is not empty',
        noComparison: true,
    },
};

const SelectConditionalRight = ({ value, onChange, options }) => {
    if (!Array.isArray(value)) {
        if (Object.values(options).includes(value)) {
            value = [value];
        } else {
            value = [];
        }
    }

    const allValues = Object.keys(options).map(key => ({
        value: key,
        label: options[key],
    }));

    return (
        <>
            <CheckboxSelector
                onChange={(newValue) => {
                    // Filter newValue to only include valid options
                    newValue = newValue.filter(v =>
                        allValues.find(av => av.value === v),
                    );
                    onChange(newValue);
                }}
                selectedValues={value}
                allValues={allValues}
                className="w-full"
            />
        </>

        // <Select
        //     options={options}
        //     onChange={onChange}
        //     value={value}
        // />
    );
};

const SingleCondition = ({ schema, condition, setCondition }) => {
    const rightInputRef = useRef();

    function remove(ev) {
        ev.preventDefault();
        setCondition(null);
    }

    const categorizedSchema = {};
    schema.forEach(s => {
        categorizedSchema[s.category] = [
            ...(categorizedSchema[s.category] || []),
            s,
        ];
    });

    const itemSchema = schema.find(s => s.key === condition.left);
    const options = itemSchema?.options || [];
    let optionOpts = {};
    if (Array.isArray(options)) {
        options.forEach(o => (optionOpts[o] = o));
    } else {
        optionOpts = options;
    }

    const needsComparison = !operators[condition.operator]?.noComparison;
    const hasOptions = needsComparison && Object.keys(optionOpts).length > 0;
    const isLeftError = !condition?.left && condition?.right;

    return (
        <div>
            <div className="flex items-start flex-wrap gap-[8px] min-[540px]:flex-nowrap">
                <select
                    value={condition.left}
                    onChange={e =>
                        setCondition({ ...condition, left: e.target.value })
                    }
                    className={`w-full md:w-auto max-w-56 ${isLeftError ? 'border-red-700' : ''}`}
                >
                    <option value="">Choose...</option>
                    {Object.keys(categorizedSchema).map(key => (
                        <optgroup key={key} label={key}>
                            {categorizedSchema[key].map(element => (
                                <option value={element.key} key={element.key}>
                                    {element.label}
                                </option>
                            ))}
                        </optgroup>
                    ))}
                </select>
                <select
                    value={condition.operator}
                    onChange={e => {
                        const newOperator = e.target.value;
                        if (condition?.operator) {
                            rightInputRef.current?.focus();
                        }
                        setCondition({
                            ...condition,
                            operator: newOperator,
                            ...(operators[newOperator]?.noComparison
                                ? { right: '' }
                                : {}),
                        });
                    }}
                    className=""
                >
                    {Object.keys(operators).map(op => (
                        <option value={op} key={op}>
                            {operators[op].name}
                        </option>
                    ))}
                </select>
                {hasOptions && (
                    <SelectConditionalRight
                        options={optionOpts}
                        value={condition?.right}
                        onChange={newValue =>
                            setCondition({ ...condition, right: newValue })
                        }
                    />
                )}
                {!hasOptions && needsComparison && (
                    <input
                        className="!w-[150px] sm:!w-auto"
                        ref={rightInputRef}
                        type="text"
                        placeholder="Comparison"
                        value={
                            typeof condition.right === 'string'
                                ? condition.right
                                : ''
                        }
                        onChange={e =>
                            setCondition({ ...condition, right: e.target.value })
                        }
                    />
                )}

                <a href="#" onClick={remove} className="ml-2 mt-1">
                    <TrashIcon className="text-red-700 w-5 h-5 inline" />
                </a>
            </div>
            {isLeftError && (
                <ErrorMessage className="mb-2">Please select a condition</ErrorMessage>
            )}
        </div>
    );
};

const ConditionalBuilder = ({ schema, conditional, setConditional }) => {
    useEffect(() => {
        if (!conditional) {
            setConditional({ andor: 'and', conditions: [] });
        }
    }, [conditional]);

    function setCondition(newCondition, idx) {
        const newConditional = { ...conditional };
        if (newCondition) {
            newConditional.conditions[idx] = newCondition;
        } else {
            // Remove it
            newConditional.conditions.splice(idx, 1);
        }
        setConditional(newConditional);
    }

    function addCondition() {
        const newConditional = { ...conditional };
        newConditional.conditions = [
            ...(conditional?.conditions || []),
            {
                left: '',
                operator: '=',
                right: '',
            },
        ];
        setConditional(newConditional);
    }

    return (
        <div>
            <div>
                {!!conditional?.conditions &&
                    conditional?.conditions.map((condition, idx) => (
                        <div key={idx}>
                            {idx === 1 && (
                                <select
                                    className="my-2"
                                    value={conditional?.andor}
                                    onChange={e =>
                                        setConditional({
                                            ...conditional,
                                            andor: e.target.value,
                                        })
                                    }
                                >
                                    <option value="and">And</option>
                                    <option value="or">Or</option>
                                </select>
                            )}
                            {idx > 1 && (
                                <div className="my-2">{conditional.andor}</div>
                            )}
                            <SingleCondition
                                schema={schema}
                                condition={condition}
                                setCondition={newCondition =>
                                    setCondition(newCondition, idx)
                                }
                            />
                        </div>
                    ))}
                <button
                    className="mt-2 text-sm btn-light-pill-sm"
                    type="button"
                    onClick={addCondition}
                >
                    + Add Condition
                </button>
            </div>
        </div>
    );
};

export default ConditionalBuilder;
